import { useEffect, useRef, type ReactNode } from 'react'

import { Icon } from '@/components/icons/Icon'

import styles from './Sheet.module.css'

/**
 * Bottom sheet built on <dialog> (focus trap, Esc to close, backdrop for free). A visible close
 * button sits beside the title: on phones the wide sheets leave almost no backdrop to tap.
 */
export function Sheet({
  title,
  onClose,
  footer,
  wide = false,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  /** From 900 px: a centred dialog up to 960 px wide instead of a bottom sheet. */
  wide?: boolean
  /** Pinned under the scrolling content (not sticky: that mis-paints on iOS after content swaps). */
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dlg = ref.current
    if (!dlg || dlg.open) return
    if (typeof dlg.showModal === 'function') dlg.showModal()
    else dlg.setAttribute('open', '') // very old browsers / jsdom
  }, [])

  const head = (
    <div className={styles.head}>
      <h2 className={styles.title}>{title}</h2>
      <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
        <Icon name="close" size={20} />
      </button>
    </div>
  )

  return (
    // Esc / focus trap come from <dialog>; the click handler only adds "tap backdrop to close".
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={ref}
      className={`${styles.sheet} ${footer ? styles.withFooter : ''} ${wide ? styles.wide : ''}`}
      aria-label={title}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
    >
      {footer ? (
        <>
          <div className={styles.body}>
            <div className={styles.grip} />
            {head}
            {children}
          </div>
          <div className={styles.footer}>{footer}</div>
        </>
      ) : (
        <>
          <div className={styles.grip} />
          {head}
          {children}
        </>
      )}
    </dialog>
  )
}
